import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { GameDate } from '../types/events';
import type { SupplyDemandPoint, ProductSupplyDemandHistory } from '../schemas/gameSave';

// Maximum number of data points to retain per product (90 days for 3-month view)
const MAX_HISTORY_POINTS = 90;

interface SupplyDemandHistoryStore {
  // State
  productHistories: Map<string, SupplyDemandPoint[]>;

  // Actions
  recordDataPoint: (
    productId: string,
    date: GameDate,
    supply: number,
    demand: number,
    unitsSold: number
  ) => void;

  getProductHistory: (productId: string, days?: number) => SupplyDemandPoint[];

  clearProductHistory: (productId: string) => void;

  clearAllHistory: () => void;

  // Hydration from save
  hydrateFromSave: (saveData: { productHistories: ProductSupplyDemandHistory[] } | undefined) => void;

  // Export for save
  exportForSave: () => { productHistories: ProductSupplyDemandHistory[] };

  // Reset
  reset: () => void;
}

// Helper to convert Map to array for serialization
const mapToArray = (map: Map<string, SupplyDemandPoint[]>): ProductSupplyDemandHistory[] => {
  const result: ProductSupplyDemandHistory[] = [];
  map.forEach((history, productId) => {
    result.push({ productId, history });
  });
  return result;
};

// Helper to convert array to Map for deserialization
const arrayToMap = (arr: ProductSupplyDemandHistory[]): Map<string, SupplyDemandPoint[]> => {
  const map = new Map<string, SupplyDemandPoint[]>();
  arr.forEach(item => {
    map.set(item.productId, item.history);
  });
  return map;
};

export const useSupplyDemandHistoryStore = create<SupplyDemandHistoryStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      productHistories: new Map(),

      // Record a new data point for a product
      recordDataPoint: (productId, date, supply, demand, unitsSold) => {
        // Validate inputs - ignore invalid data
        if (supply < 0 || demand < 0 || unitsSold < 0) {
          return;
        }

        set(state => {
          const newHistories = new Map(state.productHistories);
          const existingHistory = newHistories.get(productId) || [];

          // Create the new data point
          const newPoint: SupplyDemandPoint = {
            date,
            supply,
            demand,
            unitsSold
          };

          // Add new point and trim to max size
          const updatedHistory = [...existingHistory, newPoint];
          if (updatedHistory.length > MAX_HISTORY_POINTS) {
            updatedHistory.shift(); // Remove oldest point
          }

          newHistories.set(productId, updatedHistory);

          return { productHistories: newHistories };
        }, false, 'recordDataPoint');
      },

      // Get history for a product, optionally limited to N most recent days
      getProductHistory: (productId, days) => {
        const history = get().productHistories.get(productId) || [];
        if (days && days < history.length) {
          return history.slice(-days);
        }
        return history;
      },

      // Clear history for a specific product
      clearProductHistory: (productId) => {
        set(state => {
          const newHistories = new Map(state.productHistories);
          newHistories.delete(productId);
          return { productHistories: newHistories };
        }, false, 'clearProductHistory');
      },

      // Clear all history
      clearAllHistory: () => {
        set({ productHistories: new Map() }, false, 'clearAllHistory');
      },

      // Hydrate from save data
      hydrateFromSave: (saveData) => {
        if (!saveData?.productHistories) {
          return;
        }

        set({
          productHistories: arrayToMap(saveData.productHistories)
        }, false, 'hydrateFromSave');
      },

      // Export for save
      exportForSave: () => {
        return {
          productHistories: mapToArray(get().productHistories)
        };
      },

      // Reset to initial state
      reset: () => {
        set({
          productHistories: new Map()
        }, false, 'reset');
      }
    }),
    { name: 'SupplyDemandHistoryStore' }
  )
);

// Selector to get display days based on product age
export const getDisplayDaysForProduct = (
  releaseDate: GameDate | undefined,
  currentDate: GameDate
): number => {
  if (!releaseDate) return 30;

  // Calculate months since launch
  const yearsDiff = currentDate.year - releaseDate.year;
  const monthsDiff = (currentDate.month - releaseDate.month) + (yearsDiff * 12);

  // If product is 3+ months old, show 90 days; otherwise show 30 days
  return monthsDiff >= 3 ? 90 : 30;
};
